import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config';

const ConsentFormsManager = () => {
  // Main states
  const [forms, setForms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeView, setActiveView] = useState('list'); // 'list', 'create', 'edit', 'preview', 'responses'
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [responses, setResponses] = useState([]);
  const [selectedResponseId, setSelectedResponseId] = useState(null);
  
  // Student and year data
  const [allStudents, setAllStudents] = useState([]);
  const [allYearGroups, setAllYearGroups] = useState([]);
  const [studentFilterForm, setStudentFilterForm] = useState('');
  const [studentFilterYear, setStudentFilterYear] = useState('');
  
  // Assignment modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [expandedYears, setExpandedYears] = useState([]);
  const [expandedForms, setExpandedForms] = useState([]);
  
  // Form creation/editing state
  const [currentForm, setCurrentForm] = useState({
    title: '',
    description: '',
    logo_url: '',
    target_years: [],
    target_students: [],
    questions: []
  });
  
  // Question being added/edited
  const [currentQuestion, setCurrentQuestion] = useState({
    id: null,
    type: 'text', // text, boolean, single_choice, multiple_choice, paragraph, upload
    question: '',
    required: false,
    options: [],
    isams_field_name: null,  // iSAMS custom field mapping
    isams_document_upload: null  // iSAMS document upload configuration: { categoryId, documentNamePattern, description }
  });
  const [editingQuestionId, setEditingQuestionId] = useState(null);

  // iSAMS integration states
  const [showISAMSModal, setShowISAMSModal] = useState(false);
  const [isamsCustomFields, setISAMSCustomFields] = useState([]);
  const [isamsLoading, setISAMSLoading] = useState(false);
  const [selectedQuestionForISAMS, setSelectedQuestionForISAMS] = useState(null);
  const [isamsSearchQuery, setISAMSSearchQuery] = useState('');

  // iSAMS document upload states
  const [showDocumentUploadModal, setShowDocumentUploadModal] = useState(false);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentUploadData, setDocumentUploadData] = useState({
    documentName: '',
    categoryId: '',
    description: '',
    file: null
  });
  const [showDocumentConfigModal, setShowDocumentConfigModal] = useState(false);
  const [selectedQuestionForDocument, setSelectedQuestionForDocument] = useState(null);
  const [documentConfig, setDocumentConfig] = useState({
    categoryId: '65', // Default to "Consent form"
    documentNamePattern: '{student_id}_{form_title}',
    description: ''
  });

  // Debug: Monitor modal state changes
  useEffect(() => {
    console.log('[iSAMS] Modal state changed:', showISAMSModal);
    console.log('[iSAMS] Custom fields count:', isamsCustomFields.length);
  }, [showISAMSModal, isamsCustomFields]);

  // Load all forms on mount
  useEffect(() => {
    loadForms();
    loadStudentsAndYears();
    loadDocumentCategories();
  }, []);

  const loadStudentsAndYears = async () => {
    try {
      console.log('[ConsentForms] Loading students and years...');
      const response = await axios.get(buildApiUrl('/api/consent-forms/students-years'));
      console.log('[ConsentForms] Students and years response:', response.data);
      if (response.data.success) {
        console.log('[ConsentForms] Students count:', response.data.students?.length);
        console.log('[ConsentForms] Year groups:', response.data.year_groups);
        console.log('[ConsentForms] Sample student:', response.data.students?.[0]);
        setAllStudents(response.data.students || []);
        setAllYearGroups(response.data.year_groups || []);
      } else {
        console.error('[ConsentForms] API returned error:', response.data.error);
      }
    } catch (error) {
      console.error('[ConsentForms] Failed to load students and years:', error);
      console.error('[ConsentForms] Error details:', error.response?.data);
    }
  };

  const loadDocumentCategories = async () => {
    try {
      console.log('[ConsentForms] Loading iSAMS document categories...');
      const response = await axios.get(buildApiUrl('/api/isams/document-categories'));
      console.log('[ConsentForms] Document categories response:', response.data);
      if (response.data.success) {
        setDocumentCategories(response.data.categories || []);
        console.log('[ConsentForms] Loaded', response.data.categories?.length, 'document categories');
        console.log('[ConsentForms] First category:', response.data.categories?.[0]);
      } else {
        console.error('[ConsentForms] API returned error:', response.data.error);
      }
    } catch (error) {
      console.error('[ConsentForms] Failed to load document categories:', error);
      console.error('[ConsentForms] Error details:', error.response?.data);
    }
  };

  const openDocumentUploadModal = () => {
    if (!selectedResponse) return;
    setDocumentUploadData({
      documentName: `${selectedResponse.student_school_id}`,
      categoryId: '65', // Default to "Consent form" category
      description: `Consent Form: ${currentForm.title}`,
      file: null
    });
    setShowDocumentUploadModal(true);
  };

  const uploadDocumentToISAMS = async () => {
    if (!selectedResponse || !documentUploadData.file) {
      alert('Please select a file to upload');
      return;
    }

    setUploadingDocument(true);
    try {
      // Get file extension from uploaded file
      const fileExtension = documentUploadData.file.name.split('.').pop();
      const fullFileName = `${documentUploadData.documentName}.${fileExtension}`;

      // Create form data
      const formData = new FormData();
      formData.append('file', documentUploadData.file);
      formData.append('name', fullFileName);
      formData.append('category_id', documentUploadData.categoryId);
      formData.append('description', documentUploadData.description);

      console.log('[iSAMS] Uploading document:', {
        schoolId: selectedResponse.student_school_id,
        fileName: fullFileName,
        categoryId: documentUploadData.categoryId,
        description: documentUploadData.description,
        fileSize: documentUploadData.file.size
      });

      const response = await axios.post(
        buildApiUrl(`/api/isams/upload-document-file/${selectedResponse.student_school_id}`),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        alert('Document uploaded to iSAMS successfully!');
        setShowDocumentUploadModal(false);
        setDocumentUploadData({
          documentName: '',
          categoryId: '',
          description: '',
          file: null
        });
      } else {
        alert('Failed to upload document: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('[iSAMS] Document upload failed:', error);
      alert('Failed to upload document: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploadingDocument(false);
    }
  };

  const loadForms = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(buildApiUrl('/api/consent-forms'));
      if (response.data.success) {
        setForms(response.data.forms || []);
      }
    } catch (error) {
      console.error('Failed to load consent forms:', error);
      alert('Failed to load consent forms');
    } finally {
      setIsLoading(false);
    }
  };

  const loadResponses = async (formId) => {
    setIsLoading(true);
    try {
      console.log('[ConsentForms] Loading responses for form:', formId);
      const url = buildApiUrl(`/api/consent-forms/responses?form_id=${formId}`);
      console.log('[ConsentForms] Calling URL:', url);
      const response = await axios.get(url);
      console.log('[ConsentForms] API Response:', response.data);
      if (response.data.success) {
        console.log('[ConsentForms] Responses found:', response.data.responses.length);
        setResponses(response.data.responses || []);
        if (response.data.responses.length > 0) {
          setSelectedResponseId(response.data.responses[0].id);
          console.log('[ConsentForms] Selected first response:', response.data.responses[0].id);
        }
      } else {
        console.error('[ConsentForms] API returned error:', response.data.error);
      }
    } catch (error) {
      console.error('[ConsentForms] Failed to load responses:', error);
      console.error('[ConsentForms] Error response:', error.response?.data);
      alert('Failed to load responses: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const viewResponses = (formId) => {
    const form = forms.find(f => f.id === formId);
    if (form) {
      setCurrentForm(form);
      setSelectedFormId(formId);
      setActiveView('responses');
      loadResponses(formId);
    }
  };

  const startNewForm = () => {
    setCurrentForm({
      title: '',
      description: '',
      logo_url: '',
      target_years: [],
      target_students: [],
      questions: []
    });
    setCurrentQuestion({
      id: null,
      type: 'text',
      question: '',
      required: false,
      options: []
    });
    setEditingQuestionId(null);
    setSelectedFormId(null); // Clear selected form ID for new form
    setActiveView('create');
  };

  const assignStudents = async (formId) => {
    console.log('[ConsentForms] assignStudents called with formId:', formId);
    console.log('[ConsentForms] Current allStudents length:', allStudents.length);
    console.log('[ConsentForms] Current allYearGroups:', allYearGroups);
    console.log('[ConsentForms] Current showAssignModal:', showAssignModal);
    
    const form = forms.find(f => f.id === formId);
    console.log('[ConsentForms] Found form:', form);
    
    if (form) {
      // Reload students if not already loaded
      if (allStudents.length === 0) {
        console.log('[ConsentForms] Students not loaded, loading now...');
        await loadStudentsAndYears();
        console.log('[ConsentForms] After loading - allStudents length:', allStudents.length);
      }
      
      setCurrentForm({
        ...form,
        target_students: form.target_students || [],
        target_years: form.target_years || []
      });
      setSelectedFormId(formId);
      console.log('[ConsentForms] About to set showAssignModal to true');
      setShowAssignModal(true);
      console.log('[ConsentForms] showAssignModal set to true');
      
      // Force a re-render check
      setTimeout(() => {
        console.log('[ConsentForms] After timeout - showAssignModal should be:', showAssignModal);
      }, 100);
    } else {
      console.error('[ConsentForms] Form not found for id:', formId);
    }
  };

  const saveAssignment = async () => {
    if (!selectedFormId) return;
    
    setIsLoading(true);
    try {
      const response = await axios.put(buildApiUrl(`/api/consent-forms/${selectedFormId}`), {
        ...currentForm,
        target_students: currentForm.target_students,
        target_years: currentForm.target_years
      });
      
      if (response.data.success) {
        alert('✅ Students assigned successfully!');
        await loadForms();
        setShowAssignModal(false);
      }
    } catch (error) {
      console.error('[ConsentForms] Failed to save assignment:', error);
      alert('❌ Failed to save assignment: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const editForm = (formId) => {
    const form = forms.find(f => f.id === formId);
    if (form) {
      setCurrentForm(form);
      setSelectedFormId(formId);
      setActiveView('edit');
    }
  };

  const previewForm = (formId) => {
    const form = forms.find(f => f.id === formId);
    if (form) {
      setCurrentForm(form);
      setSelectedFormId(formId);
      setActiveView('preview');
    }
  };

  const saveForm = async () => {
    console.log('[ConsentForms] Attempting to save form...');
    console.log('[ConsentForms] Current form data:', currentForm);
    
    if (!currentForm.title.trim()) {
      alert('Please enter a form title');
      return;
    }
    
    if (currentForm.questions.length === 0) {
      alert('Please add at least one question');
      return;
    }

    setIsLoading(true);
    try {
      const endpoint = selectedFormId 
        ? `/api/consent-forms/${selectedFormId}` 
        : '/api/consent-forms';
      
      const method = selectedFormId ? 'put' : 'post';
      
      console.log('[ConsentForms] Saving to endpoint:', endpoint);
      console.log('[ConsentForms] Method:', method);
      console.log('[ConsentForms] Payload:', JSON.stringify(currentForm, null, 2));
      
      const response = await axios[method](buildApiUrl(endpoint), currentForm);
      
      console.log('[ConsentForms] Save response:', response.data);
      
      if (response.data.success) {
        alert('Form saved successfully!');
        await loadForms();
        setActiveView('list');
        setSelectedFormId(null);
      } else {
        console.error('[ConsentForms] Save failed:', response.data.error);
        alert('Failed to save form: ' + response.data.error);
      }
    } catch (error) {
      console.error('[ConsentForms] Failed to save form:', error);
      console.error('[ConsentForms] Error response:', error.response?.data);
      alert('Failed to save form: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const deleteForm = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form?')) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.delete(buildApiUrl(`/api/consent-forms/${formId}`));
      if (response.data.success) {
        alert('Form deleted successfully!');
        await loadForms();
      }
    } catch (error) {
      console.error('Failed to delete form:', error);
      alert('Failed to delete form');
    } finally {
      setIsLoading(false);
    }
  };

  const addQuestion = () => {
    if (!currentQuestion.question.trim()) {
      alert('Please enter a question');
      return;
    }

    if (['single_choice', 'multiple_choice'].includes(currentQuestion.type)) {
      if (currentQuestion.options.length < 2) {
        alert('Please add at least 2 options for choice questions');
        return;
      }
    }

    if (editingQuestionId !== null) {
      // Update existing question
      setCurrentForm(prev => ({
        ...prev,
        questions: prev.questions.map(q => 
          q.id === editingQuestionId ? { ...currentQuestion, id: q.id } : q
        )
      }));
      setEditingQuestionId(null);
    } else {
      // Add new question
      const newQuestion = {
        ...currentQuestion,
        id: Date.now()
      };
      setCurrentForm(prev => ({
        ...prev,
        questions: [...prev.questions, newQuestion]
      }));
    }

    // Reset question form
    setCurrentQuestion({
      id: null,
      type: 'text',
      question: '',
      required: false,
      options: [],
      isams_field_name: null
    });
  };

  const editQuestion = (question) => {
    setCurrentQuestion({ ...question });
    setEditingQuestionId(question.id);
  };

  const deleteQuestion = (questionId) => {
    setCurrentForm(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId)
    }));
    if (editingQuestionId === questionId) {
      setEditingQuestionId(null);
      setCurrentQuestion({
        id: null,
        type: 'text',
        question: '',
        required: false,
        options: [],
        isams_field_name: null
      });
    }
  };

  const addOption = () => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const updateOption = (index, value) => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? value : opt)
    }));
  };

  const runMigration = async () => {
    if (!window.confirm('Run database migration to add consent forms columns?\n\nThis is safe to run and only adds missing columns.')) {
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(buildApiUrl('/api/migrate-consent-forms-schema'));
      console.log('[ConsentForms] Migration response:', response.data);
      
      if (response.data.success) {
        alert('✅ Migration completed successfully!\n\n' + response.data.details.join('\n'));
      } else {
        alert('❌ Migration failed: ' + response.data.error);
      }
    } catch (error) {
      console.error('[ConsentForms] Migration error:', error);
      alert('❌ Migration failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const removeOption = (index) => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  // Assignment modal functions
  const toggleYear = (year) => {
    setExpandedYears(prev => 
      prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]
    );
  };

  const toggleForm = (formKey) => {
    setExpandedForms(prev => 
      prev.includes(formKey) ? prev.filter(f => f !== formKey) : [...prev, formKey]
    );
  };

  const isStudentSelected = (studentId) => {
    return currentForm.target_students.includes(studentId);
  };

  const toggleStudent = (studentId) => {
    setCurrentForm(prev => ({
      ...prev,
      target_students: prev.target_students.includes(studentId)
        ? prev.target_students.filter(id => id !== studentId)
        : [...prev.target_students, studentId]
    }));
  };

  const selectAllInForm = (year, form) => {
    const studentsInForm = allStudents.filter(s => s.year_group === year && s.form === form);
    const studentIds = studentsInForm.map(s => s.school_id);
    const allSelected = studentIds.every(id => currentForm.target_students.includes(id));
    
    if (allSelected) {
      // Deselect all
      setCurrentForm(prev => ({
        ...prev,
        target_students: prev.target_students.filter(id => !studentIds.includes(id))
      }));
    } else {
      // Select all
      const newIds = [...new Set([...currentForm.target_students, ...studentIds])];
      setCurrentForm(prev => ({
        ...prev,
        target_students: newIds
      }));
    }
  };

  // iSAMS Integration Functions
  const loadISAMSCustomFields = async () => {
    // Note: loading state is set by openISAMSModal before calling this
    try {
      // Use the test student ID provided by user
      const testStudentId = '1049056420712';
      console.log('[iSAMS] Loading custom fields for student:', testStudentId);
      
      const response = await axios.get(buildApiUrl(`/api/isams/custom-fields/${testStudentId}`));
      console.log('[iSAMS] Response:', response.data);
      
      if (response.data.success) {
        setISAMSCustomFields(response.data.field_names || []);
        console.log('[iSAMS] Loaded fields:', response.data.field_names);
      } else {
        console.error('[iSAMS] Error:', response.data.error);
        alert('Failed to load iSAMS custom fields: ' + response.data.error);
      }
    } catch (error) {
      console.error('[iSAMS] Failed to load custom fields:', error);
      alert('Failed to load iSAMS custom fields: ' + (error.response?.data?.error || error.message));
    } finally {
      setISAMSLoading(false);
    }
  };

  const refreshISAMSFields = async () => {
    try {
      setISAMSLoading(true);
      console.log('[iSAMS] Refreshing custom fields from API');
      
      // Call backend to refresh JSON file with fresh data
      const response = await axios.post(buildApiUrl('/api/isams/refresh-custom-fields'));
      
      if (response.data.success) {
        setISAMSCustomFields(response.data.field_names || []);
        console.log('[iSAMS] Refreshed fields:', response.data.field_names);
        alert(`Successfully refreshed ${response.data.field_names.length} custom fields from iSAMS`);
      } else {
        console.error('[iSAMS] Refresh error:', response.data.error);
        alert('Failed to refresh custom fields: ' + response.data.error);
      }
    } catch (error) {
      console.error('[iSAMS] Failed to refresh custom fields:', error);
      alert('Failed to refresh custom fields: ' + (error.response?.data?.error || error.message));
    } finally {
      setISAMSLoading(false);
    }
  };

  const openISAMSModal = async (question) => {
    console.log('[iSAMS] Opening modal for question:', question);
    setSelectedQuestionForISAMS(question);
    setISAMSSearchQuery(''); // Reset search when opening modal
    setISAMSLoading(true); // Set loading state BEFORE opening modal
    setShowISAMSModal(true);
    console.log('[iSAMS] Modal state set to true');
    await loadISAMSCustomFields();
  };

  const connectToISAMSField = (fieldName) => {
    if (selectedQuestionForISAMS) {
      // Update the question in currentForm.questions
      setCurrentForm(prev => ({
        ...prev,
        questions: prev.questions.map(q => 
          q.id === selectedQuestionForISAMS.id 
            ? { ...q, isams_field_name: fieldName }
            : q
        )
      }));
      
      // If editing current question, update it too
      if (editingQuestionId === selectedQuestionForISAMS.id) {
        setCurrentQuestion(prev => ({ ...prev, isams_field_name: fieldName }));
      }
      
      setShowISAMSModal(false);
      setSelectedQuestionForISAMS(null);
    }
  };

  const disconnectISAMSField = (questionId) => {
    setCurrentForm(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId 
          ? { ...q, isams_field_name: null }
          : q
      )
    }));
    
    if (editingQuestionId === questionId) {
      setCurrentQuestion(prev => ({ ...prev, isams_field_name: null }));
    }
  };

  const openDocumentConfigModal = (question) => {
    console.log('[Document] Opening config modal for question:', question);
    setSelectedQuestionForDocument(question);
    if (question.isams_document_upload) {
      setDocumentConfig(question.isams_document_upload);
    } else {
      setDocumentConfig({
        categoryId: '65',
        documentNamePattern: '{student_id}_{form_title}',
        description: `Document from: ${currentForm.title}`
      });
    }
    setShowDocumentConfigModal(true);
  };

  const connectDocumentUpload = () => {
    if (selectedQuestionForDocument) {
      console.log('[Document] Connecting upload config:', documentConfig);
      console.log('[Document] To question:', selectedQuestionForDocument);
      
      // Update the question in currentForm.questions
      setCurrentForm(prev => {
        const updated = {
          ...prev,
          questions: prev.questions.map(q => 
            q.id === selectedQuestionForDocument.id 
              ? { ...q, isams_document_upload: { ...documentConfig } }
              : q
          )
        };
        console.log('[Document] Updated form questions:', updated.questions);
        return updated;
      });
      
      // If editing current question, update it too
      if (editingQuestionId === selectedQuestionForDocument.id) {
        setCurrentQuestion(prev => ({ ...prev, isams_document_upload: { ...documentConfig } }));
      }
      
      setShowDocumentConfigModal(false);
      setSelectedQuestionForDocument(null);
    }
  };

  const disconnectDocumentUpload = (questionId) => {
    setCurrentForm(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId 
          ? { ...q, isams_document_upload: null }
          : q
      )
    }));
    
    if (editingQuestionId === questionId) {
      setCurrentQuestion(prev => ({ ...prev, isams_document_upload: null }));
    }
  };

  const getYearGroups = () => {
    const years = ['Pre-School', 'Reception', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 
                   'Year 6', 'Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11', 'Year 12', 'Year 13'];
    const filtered = years.filter(year => allStudents.some(s => s.year_group === year));
    console.log('[ConsentForms] getYearGroups - allStudents:', allStudents.length);
    console.log('[ConsentForms] getYearGroups - filtered years:', filtered);
    return filtered;
  };

  const getFormsInYear = (year) => {
    const forms = [...new Set(allStudents.filter(s => s.year_group === year).map(s => s.form))];
    return forms.filter(f => f).sort();
  };

  const getStudentsInForm = (year, form) => {
    return allStudents.filter(s => s.year_group === year && s.form === form).sort((a, b) => 
      a.full_name.localeCompare(b.full_name)
    );
  };

  const cancelEdit = () => {
    setActiveView('list');
    setSelectedFormId(null);
    setCurrentQuestion({
      id: null,
      type: 'text',
      question: '',
      required: false,
      options: []
    });
    setEditingQuestionId(null);
  };

  const getQuestionTypeLabel = (type) => {
    const labels = {
      text: 'Short Text',
      boolean: 'Yes/No (Boolean)',
      single_choice: 'Single Choice',
      multiple_choice: 'Multiple Choice',
      paragraph: 'Long Paragraph',
      upload: 'File Upload'
    };
    return labels[type] || type;
  };

  const getQuestionTypeIcon = (type) => {
    const icons = {
      text: '📝',
      boolean: '✅',
      single_choice: '◉',
      multiple_choice: '☑️',
      paragraph: '📄',
      upload: '📎'
    };
    return icons[type] || '❓';
  };

  // Assignment Modal - Rendered at component level so it's accessible from all views
  const renderAssignmentModal = () => {
    if (!showAssignModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
          {/* Modal Header */}
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-900">Assign Students to Form</h3>
            <button
              onClick={() => {
                setShowAssignModal(false);
                setSelectedFormId(null);
              }}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Modal Body */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="mb-4 text-sm text-gray-600">
              Selected: <span className="font-bold text-indigo-600">{currentForm.target_students?.length || 0}</span> student(s)
            </div>

            {allStudents.length === 0 ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading students...</p>
              </div>
            ) : (
              getYearGroups().map(year => (
              <div key={year} className="mb-3 border border-gray-200 rounded-lg">
                {/* Year Header */}
                <button
                  onClick={() => toggleYear(year)}
                  className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-t-lg"
                >
                  <span className="font-semibold text-gray-900 flex items-center gap-2">
                    <span>{expandedYears.includes(year) ? '▼' : '▶'}</span>
                    {year}
                  </span>
                  <span className="text-sm text-gray-600">
                    {getFormsInYear(year).length} form(s)
                  </span>
                </button>

                {/* Forms in Year */}
                {expandedYears.includes(year) && (
                  <div className="border-t border-gray-200">
                    {getFormsInYear(year).map(form => {
                      const formKey = `${year}-${form}`;
                      const studentsInForm = getStudentsInForm(year, form);
                      const allSelected = studentsInForm.every(s => isStudentSelected(s.school_id));
                      
                      return (
                        <div key={formKey} className="border-b border-gray-100 last:border-b-0">
                          {/* Form Header */}
                          <div className="flex items-center bg-white hover:bg-gray-50">
                            <button
                              onClick={() => toggleForm(formKey)}
                              className="flex-1 px-6 py-2 flex items-center justify-between text-left"
                            >
                              <span className="font-medium text-gray-800 flex items-center gap-2">
                                <span className="text-sm">{expandedForms.includes(formKey) ? '▼' : '▶'}</span>
                                Form {form}
                              </span>
                              <span className="text-sm text-gray-600">
                                {studentsInForm.length} student(s)
                              </span>
                            </button>
                            <button
                              onClick={() => selectAllInForm(year, form)}
                              className={`px-3 py-2 text-xs font-medium ${
                                allSelected 
                                  ? 'text-indigo-700 bg-indigo-100 hover:bg-indigo-200' 
                                  : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                              } rounded mr-2`}
                            >
                              {allSelected ? '✓ All' : 'Select All'}
                            </button>
                          </div>

                          {/* Students in Form */}
                          {expandedForms.includes(formKey) && (
                            <div className="bg-gray-50 px-8 py-2">
                              {studentsInForm.map(student => (
                                <label
                                  key={student.school_id}
                                  className="flex items-center py-2 hover:bg-white px-3 rounded cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isStudentSelected(student.school_id)}
                                    onChange={() => toggleStudent(student.school_id)}
                                    className="mr-3 h-4 w-4 text-indigo-600"
                                  />
                                  <span className="text-sm text-gray-800">
                                    {student.full_name} 
                                    <span className="text-gray-500 ml-2">({student.school_id})</span>
                                  </span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))
            )}
          </div>

          {/* Modal Footer */}
          <div className="px-6 py-4 border-t flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {currentForm.target_students?.length || 0} student(s) selected
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedFormId(null);
                }}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={saveAssignment}
                disabled={isLoading}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 font-medium"
              >
                {isLoading ? 'Saving...' : 'Save Assignment'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // VIEW: List all forms
  if (activeView === 'list') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">📋</span>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Consent Forms</h1>
                <p className="text-gray-600 mt-1">Create and manage consent forms for students and parents</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={runMigration}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium text-sm flex items-center gap-2"
                title="Run database migration to add missing columns"
              >
                <span>🔧</span> Run Migration
              </button>
              <button
                onClick={startNewForm}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 font-medium flex items-center gap-2"
              >
                <span>➕</span> New Form
              </button>
            </div>
          </div>
        </div>

        {/* Forms Table */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : forms.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📋</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Consent Forms Yet</h3>
            <p className="text-gray-600 mb-4">Create your first consent form to get started</p>
            <button
              onClick={startNewForm}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Create Form
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Form Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Questions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Responses
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {forms.map((form) => (
                  <tr key={form.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{form.title}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 max-w-xs truncate">
                        {form.description || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {form.questions?.length || 0} questions
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {form.created_at ? new Date(form.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        form.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {form.status || 'active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => viewResponses(form.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-indigo-300 rounded-md text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        📊 View Responses
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => assignStudents(form.id)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Assign Students"
                        >
                          👥
                        </button>
                        <button
                          onClick={() => previewForm(form.id)}
                          className="text-gray-600 hover:text-gray-900"
                          title="Preview"
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => editForm(form.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => deleteForm(form.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Assignment Modal */}
        {renderAssignmentModal()}
      </div>
    );
  }

  // VIEW: Create/Edit Form
  if (activeView === 'create' || activeView === 'edit') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">✏️</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              {activeView === 'create' ? 'Create New Form' : 'Edit Form'}
            </h1>
          </div>
          <p className="text-gray-600">
            {activeView === 'create' ? 'Build your consent form by adding questions' : 'Update your consent form'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form Editor */}
          <div className="lg:col-span-2 space-y-6">
            {/* Form Details */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Form Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Form Title *</label>
                  <input
                    type="text"
                    value={currentForm.title}
                    onChange={(e) => setCurrentForm({ ...currentForm, title: e.target.value })}
                    placeholder="e.g., Medical Consent Form"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={currentForm.description}
                    onChange={(e) => setCurrentForm({ ...currentForm, description: e.target.value })}
                    placeholder="Optional description for the form"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={3}
                  />
                </div>
                
                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Form Logo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setCurrentForm({ ...currentForm, logo_url: reader.result });
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  {currentForm.logo_url && (
                    <div className="mt-2">
                      <img src={currentForm.logo_url} alt="Logo preview" className="max-h-20 object-contain" />
                      <button
                        onClick={() => setCurrentForm({ ...currentForm, logo_url: '' })}
                        className="text-red-600 text-sm mt-1 hover:text-red-700"
                      >
                        Remove Logo
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Questions List */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Questions ({currentForm.questions.length})
              </h2>

              {currentForm.questions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">No questions added yet. Add your first question below.</p>
                </div>
              ) : (
                <div className="space-y-3 mb-6">
                  {currentForm.questions.map((question, index) => (
                    <div
                      key={question.id}
                      className={`p-4 border-2 rounded-lg flex items-start justify-between transition-colors ${
                        editingQuestionId === question.id 
                          ? 'border-indigo-500 bg-indigo-50' 
                          : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">{getQuestionTypeIcon(question.type)}</span>
                          <span className="text-xs font-medium text-gray-600">
                            {getQuestionTypeLabel(question.type)}
                          </span>
                          {question.required && (
                            <span className="text-xs font-medium text-red-600">Required</span>
                          )}
                          {question.isams_field_name && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              🔗 iSAMS: {question.isams_field_name}
                            </span>
                          )}
                          {question.isams_document_upload && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              📤 Upload to iSAMS
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-gray-900">{question.question}</p>
                        {question.options && question.options.length > 0 && (
                          <div className="mt-2 ml-6 space-y-1">
                            {question.options.map((option, i) => (
                              <p key={i} className="text-xs text-gray-600">• {option}</p>
                            ))}
                          </div>
                        )}
                        {question.isams_document_upload && (
                          <div className="mt-2 ml-6 text-xs text-gray-600">
                            <div>Category: {documentCategories.find(c => c.id === parseInt(question.isams_document_upload.categoryId))?.name || 'Unknown'}</div>
                            <div>Pattern: {question.isams_document_upload.documentNamePattern}</div>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4">
                        {question.isams_field_name ? (
                          <button
                            onClick={() => disconnectISAMSField(question.id)}
                            className="p-2 text-orange-600 hover:bg-orange-100 rounded transition-colors"
                            title="Disconnect from iSAMS field"
                          >
                            🔌
                          </button>
                        ) : (
                          <button
                            onClick={() => openISAMSModal(question)}
                            className="p-2 text-green-600 hover:bg-green-100 rounded transition-colors"
                            title="Connect to iSAMS field"
                          >
                            🔗
                          </button>
                        )}
                        {question.isams_document_upload ? (
                          <button
                            onClick={() => disconnectDocumentUpload(question.id)}
                            className="p-2 text-orange-600 hover:bg-orange-100 rounded transition-colors"
                            title="Disconnect document upload"
                          >
                            📤✕
                          </button>
                        ) : (
                          <button
                            onClick={() => openDocumentConfigModal(question)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                            title="Configure iSAMS document upload"
                          >
                            📤
                          </button>
                        )}
                        <button
                          onClick={() => editQuestion(question)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                          title="Edit question"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => deleteQuestion(question.id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                          title="Delete question"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Question Builder */}
          <div className="bg-white rounded-lg shadow p-6 h-fit">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {editingQuestionId !== null ? 'Edit Question' : 'Add Question'}
            </h2>

            <div className="space-y-4">
              {/* Question Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Question *</label>
                <textarea
                  value={currentQuestion.question}
                  onChange={(e) => setCurrentQuestion({ ...currentQuestion, question: e.target.value })}
                  placeholder="Enter your question"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  rows={3}
                />
              </div>

              {/* Question Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Question Type *</label>
                <select
                  value={currentQuestion.type}
                  onChange={(e) => setCurrentQuestion({ 
                    ...currentQuestion, 
                    type: e.target.value,
                    options: ['single_choice', 'multiple_choice'].includes(e.target.value) 
                      ? currentQuestion.options 
                      : []
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                >
                  <option value="text">📝 Short Text</option>
                  <option value="boolean">✅ Yes/No</option>
                  <option value="single_choice">◉ Single Choice</option>
                  <option value="multiple_choice">☑️ Multiple Choice</option>
                  <option value="paragraph">📄 Long Paragraph</option>
                  <option value="upload">📎 File Upload</option>
                </select>
              </div>

              {/* Options for Choice Questions */}
              {['single_choice', 'multiple_choice'].includes(currentQuestion.type) && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Options *</label>
                    <button
                      onClick={addOption}
                      className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      + Add Option
                    </button>
                  </div>
                  <div className="space-y-2">
                    {currentQuestion.options.map((option, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => updateOption(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        />
                        <button
                          onClick={() => removeOption(index)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Required Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="required"
                  checked={currentQuestion.required}
                  onChange={(e) => setCurrentQuestion({ ...currentQuestion, required: e.target.checked })}
                  className="w-4 h-4 border border-gray-300 rounded"
                />
                <label htmlFor="required" className="text-sm font-medium text-gray-700">
                  Make this question required
                </label>
              </div>

              {/* Action Buttons */}
              <div className="pt-4 border-t space-y-2">
                <button
                  onClick={addQuestion}
                  className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                >
                  {editingQuestionId !== null ? 'Update Question' : 'Add Question'}
                </button>
                {editingQuestionId !== null && (
                  <button
                    onClick={() => {
                      setEditingQuestionId(null);
                      setCurrentQuestion({
                        id: null,
                        type: 'text',
                        question: '',
                        required: false,
                        options: []
                      });
                    }}
                    className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Save/Cancel Buttons */}
        <div className="flex gap-4 justify-end pt-6 border-t">
          <button
            onClick={cancelEdit}
            className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={saveForm}
            disabled={isLoading}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition-colors font-medium"
          >
            {isLoading ? 'Saving...' : 'Save Form'}
          </button>
        </div>

        {/* Assignment Modal */}
        {renderAssignmentModal()}

        {/* iSAMS Field Selection Modal */}
        {showISAMSModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{zIndex: 9999}}>
            {console.log('[iSAMS] Rendering modal JSX, loading:', isamsLoading, 'fields:', isamsCustomFields.length)}
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Connect to iSAMS Field</h3>
                <button
                  onClick={() => {
                    setShowISAMSModal(false);
                    setSelectedQuestionForISAMS(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-4">
                {selectedQuestionForISAMS && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm font-medium text-blue-900 mb-1">Question:</p>
                    <p className="text-sm text-blue-800">{selectedQuestionForISAMS.question}</p>
                  </div>
                )}

                {isamsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  </div>
                ) : isamsCustomFields.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-2">No custom fields found</p>
                    <p className="text-xs text-gray-400">Make sure iSAMS custom fields are configured</p>
                  </div>
                ) : (
                  <div>
                    <div className="mb-4 space-y-3">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Search fields..."
                          value={isamsSearchQuery}
                          onChange={(e) => setISAMSSearchQuery(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        />
                        <button
                          onClick={refreshISAMSFields}
                          disabled={isamsLoading}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 font-medium text-sm flex items-center gap-2"
                          title="Refresh from iSAMS API"
                        >
                          🔄 Refresh
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">
                        Showing {isamsCustomFields.filter(f => f.toLowerCase().includes(isamsSearchQuery.toLowerCase())).length} of {isamsCustomFields.length} fields
                      </p>
                    </div>
                    <div className="max-h-64 overflow-y-auto space-y-2">
                      {isamsCustomFields
                        .filter(fieldName => fieldName.toLowerCase().includes(isamsSearchQuery.toLowerCase()))
                        .map((fieldName, index) => (
                        <button
                          key={index}
                          onClick={() => connectToISAMSField(fieldName)}
                          className="w-full px-4 py-2 text-left border border-gray-300 rounded-lg hover:bg-indigo-50 hover:border-indigo-500 transition-colors"
                        >
                          <span className="text-sm font-medium text-gray-900">{fieldName}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t flex justify-end">
                <button
                  onClick={() => {
                    setShowISAMSModal(false);
                    setSelectedQuestionForISAMS(null);
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Document Upload Configuration Modal */}
        {showDocumentConfigModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{zIndex: 9999}}>
            <div className="bg-white rounded-lg shadow-xl max-w-xl w-full">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Configure iSAMS Document Upload</h3>
                <button
                  onClick={() => {
                    setShowDocumentConfigModal(false);
                    setSelectedQuestionForDocument(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-4 space-y-4">
                {selectedQuestionForDocument && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm font-medium text-blue-900 mb-1">Question:</p>
                    <p className="text-sm text-blue-800">{selectedQuestionForDocument.question}</p>
                    <p className="text-xs text-blue-700 mt-1">When parent uploads a file, it will be automatically sent to iSAMS</p>
                  </div>
                )}

                {/* Document Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={documentConfig.categoryId}
                    onChange={(e) => setDocumentConfig({...documentConfig, categoryId: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Select a category...</option>
                    {documentCategories.length === 0 ? (
                      <option disabled>Loading categories...</option>
                    ) : (
                      documentCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))
                    )}
                  </select>
                  {documentCategories.length === 0 && (
                    <p className="text-xs text-red-500 mt-1">
                      Categories not loaded. Check console for errors.
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    This category will be used when uploading the document to iSAMS
                  </p>
                </div>

                {/* Document Name Pattern */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document Name Pattern <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={documentConfig.documentNamePattern}
                    onChange={(e) => setDocumentConfig({...documentConfig, documentNamePattern: e.target.value})}
                    placeholder="e.g., {student_id}_{form_title}"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Available variables: <code className="bg-gray-100 px-1 rounded">{'{student_id}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{student_name}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{form_title}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{date}'}</code>
                  </p>
                  <p className="text-xs text-gray-500">
                    File extension will be added automatically from uploaded file
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={documentConfig.description}
                    onChange={(e) => setDocumentConfig({...documentConfig, description: e.target.value})}
                    placeholder="Optional description..."
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* Preview */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-700 mb-2">Example:</div>
                  <div className="text-sm text-gray-600">
                    When student <strong>12345 (John Doe)</strong> submits form <strong>"{currentForm.title}"</strong> on <strong>2024-12-02</strong>:
                    <div className="mt-2 bg-white p-2 rounded border border-gray-300 font-mono text-xs">
                      {documentConfig.documentNamePattern
                        .replace('{student_id}', '12345')
                        .replace('{student_name}', 'John_Doe')
                        .replace('{form_title}', currentForm.title?.replace(/\s+/g, '_'))
                        .replace('{date}', '2024-12-02')
                      }.pdf
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDocumentConfigModal(false);
                    setSelectedQuestionForDocument(null);
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={connectDocumentUpload}
                  disabled={!documentConfig.categoryId || !documentConfig.documentNamePattern}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                >
                  Connect
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // VIEW: Responses (Split Pane)
  if (activeView === 'responses') {
    const selectedResponse = responses.find(r => r.id === selectedResponseId);
    
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setActiveView('list')}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              ← Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Responses: {currentForm.title}</h1>
          </div>
          <div className="text-sm text-gray-600">
            Total Submissions: <span className="font-semibold text-gray-900">{responses.length}</span>
          </div>
        </div>

        {/* Split Pane Layout */}
        <div className="flex gap-4 h-[calc(100vh-200px)]">
          {/* Left Pane: Submissions List */}
          <div className="w-1/3 bg-white rounded-lg shadow overflow-hidden flex flex-col">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
              <h2 className="font-semibold text-gray-900">Submissions ({responses.length})</h2>
              {responses.length > 0 && (
                <div className="flex gap-2">
                  <button
                    onClick={() => window.open(buildApiUrl(`/api/consent-forms/${selectedFormId}/export-excel`), '_blank')}
                    className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded flex items-center gap-1"
                    title="Export all to Excel"
                  >
                    📊 Excel
                  </button>
                  <button
                    onClick={() => window.open(buildApiUrl(`/api/consent-forms/${selectedFormId}/export-pdf`), '_blank')}
                    className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded flex items-center gap-1"
                    title="Export all to PDF"
                  >
                    📄 PDF
                  </button>
                </div>
              )}
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : responses.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <div className="text-6xl mb-4">📭</div>
                  <p className="text-gray-600">No responses yet</p>
                  <p className="text-sm text-gray-500 mt-2">Responses will appear here once parents submit the form</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {responses.map((response) => (
                    <div
                      key={response.id}
                      onClick={() => setSelectedResponseId(response.id)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedResponseId === response.id ? 'bg-indigo-50 border-l-4 border-indigo-600' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <div className="font-medium text-gray-900">{response.student_name}</div>
                        <div className="text-xs text-gray-500">
                          {response.submitted_at ? new Date(response.submitted_at).toLocaleDateString() : ''}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">ID: {response.student_school_id}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        Parent: {response.parent_name || response.parent_email}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Pane: Response Preview */}
          <div className="flex-1 bg-white rounded-lg shadow overflow-hidden flex flex-col">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="font-semibold text-gray-900">Response Details</h2>
              {selectedResponse && (
                <div className="flex gap-2">
                  <button
                    onClick={() => window.open(buildApiUrl(`/api/consent-forms/${selectedFormId}/export-pdf?response_id=${selectedResponse.id}`), '_blank')}
                    className="text-sm bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded flex items-center gap-2"
                  >
                    📄 Export PDF
                  </button>
                  <button
                    onClick={openDocumentUploadModal}
                    className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center gap-2"
                    title="Upload document to iSAMS"
                  >
                    📤 Upload to iSAMS
                  </button>
                </div>
              )}
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {selectedResponse ? (
                <div className="space-y-6">
                  {/* Submission Info */}
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-medium text-blue-900 uppercase tracking-wide mb-1">Student</div>
                        <div className="text-sm font-semibold text-blue-900">{selectedResponse.student_name}</div>
                        <div className="text-xs text-blue-700">ID: {selectedResponse.student_school_id}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-blue-900 uppercase tracking-wide mb-1">Parent</div>
                        <div className="text-sm font-semibold text-blue-900">{selectedResponse.parent_name || 'N/A'}</div>
                        <div className="text-xs text-blue-700">{selectedResponse.parent_email}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-blue-900 uppercase tracking-wide mb-1">Submitted</div>
                        <div className="text-sm text-blue-900">
                          {selectedResponse.submitted_at ? new Date(selectedResponse.submitted_at).toLocaleString() : 'N/A'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-blue-900 uppercase tracking-wide mb-1">Submission ID</div>
                        <div className="text-xs text-blue-700 font-mono">#{selectedResponse.id}</div>
                      </div>
                    </div>
                  </div>

                  {/* Questions and Answers */}
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Responses</h3>
                    {currentForm.questions.map((question, index) => {
                      const answer = selectedResponse.responses[question.id];
                      return (
                        <div key={question.id} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-start gap-3 mb-3">
                            <span className="bg-indigo-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0">
                              {index + 1}
                            </span>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 mb-1">{question.question}</div>
                              <div className="text-xs text-gray-500">
                                {getQuestionTypeLabel(question.type)}
                                {question.required && <span className="text-red-500 ml-1">* Required</span>}
                              </div>
                            </div>
                          </div>
                          
                          <div className="ml-10 mt-3 p-3 bg-white rounded border border-gray-200">
                            {answer ? (
                              Array.isArray(answer) ? (
                                <ul className="list-disc list-inside space-y-1">
                                  {answer.map((item, idx) => (
                                    <li key={idx} className="text-gray-900">{item}</li>
                                  ))}
                                </ul>
                              ) : (
                                <div className="text-gray-900">{String(answer)}</div>
                              )
                            ) : (
                              <div className="text-gray-400 italic">No answer provided</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="text-6xl mb-4">📄</div>
                    <p className="text-gray-600">Select a submission to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Assignment Modal */}
        {renderAssignmentModal()}

        {/* Document Upload Modal */}
        {showDocumentUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Upload Document to iSAMS</h3>
                <button
                  onClick={() => setShowDocumentUploadModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Student Info */}
                {selectedResponse && (
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                    <div className="text-sm">
                      <div className="font-semibold text-blue-900">{selectedResponse.student_name}</div>
                      <div className="text-blue-700">School ID: {selectedResponse.student_school_id}</div>
                      <div className="text-blue-700">Form: {currentForm.title}</div>
                    </div>
                  </div>
                )}

                {/* Document Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={documentUploadData.documentName}
                    onChange={(e) => setDocumentUploadData({...documentUploadData, documentName: e.target.value})}
                    placeholder="e.g., consent_form_2024"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The file extension will be added automatically from your uploaded file
                  </p>
                </div>

                {/* Category Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={documentUploadData.categoryId}
                    onChange={(e) => setDocumentUploadData({...documentUploadData, categoryId: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a category...</option>
                    {documentCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={documentUploadData.description}
                    onChange={(e) => setDocumentUploadData({...documentUploadData, description: e.target.value})}
                    placeholder="Optional description..."
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    File <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    onChange={(e) => setDocumentUploadData({...documentUploadData, file: e.target.files[0]})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {documentUploadData.file && (
                    <p className="text-sm text-gray-600 mt-2">
                      Selected: {documentUploadData.file.name} ({(documentUploadData.file.size / 1024).toFixed(2)} KB)
                    </p>
                  )}
                </div>

                {/* Preview of what will be sent */}
                {documentUploadData.file && documentUploadData.documentName && documentUploadData.categoryId && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Upload Summary:</div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>
                        <span className="font-medium">Final filename:</span>{' '}
                        {documentUploadData.documentName}.{documentUploadData.file.name.split('.').pop()}
                      </div>
                      <div>
                        <span className="font-medium">Category:</span>{' '}
                        {documentCategories.find(c => c.id === parseInt(documentUploadData.categoryId))?.name || 'Unknown'}
                      </div>
                      <div>
                        <span className="font-medium">Description:</span>{' '}
                        {documentUploadData.description || '(none)'}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowDocumentUploadModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={uploadingDocument}
                >
                  Cancel
                </button>
                <button
                  onClick={uploadDocumentToISAMS}
                  disabled={!documentUploadData.file || !documentUploadData.documentName || !documentUploadData.categoryId || uploadingDocument}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {uploadingDocument ? 'Uploading...' : 'Upload to iSAMS'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // VIEW: Preview Form
  if (activeView === 'preview') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => setActiveView('list')}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            ← Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Preview: {currentForm.title}</h1>
        </div>

        {/* Preview Card */}
        <div className="bg-white rounded-lg shadow p-8 max-w-2xl">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{currentForm.title}</h2>
            {currentForm.description && (
              <p className="text-gray-600">{currentForm.description}</p>
            )}
          </div>

          <div className="border-t pt-6 space-y-8">
            {currentForm.questions.map((question, index) => (
              <div key={question.id} className="space-y-3">
                <label className="block font-medium text-gray-900">
                  {index + 1}. {question.question}
                  {question.required && <span className="text-red-600 ml-1">*</span>}
                </label>

                {question.type === 'text' && (
                  <input
                    type="text"
                    placeholder="Enter your answer"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    disabled
                  />
                )}

                {question.type === 'boolean' && (
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2">
                      <input type="radio" name={`q${question.id}`} disabled />
                      <span className="text-gray-700">Yes</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name={`q${question.id}`} disabled />
                      <span className="text-gray-700">No</span>
                    </label>
                  </div>
                )}

                {question.type === 'single_choice' && (
                  <div className="space-y-2">
                    {question.options.map((option, i) => (
                      <label key={i} className="flex items-center gap-2">
                        <input type="radio" name={`q${question.id}`} disabled />
                        <span className="text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {question.type === 'multiple_choice' && (
                  <div className="space-y-2">
                    {question.options.map((option, i) => (
                      <label key={i} className="flex items-center gap-2">
                        <input type="checkbox" disabled />
                        <span className="text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {question.type === 'paragraph' && (
                  <textarea
                    placeholder="Enter your answer"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={4}
                    disabled
                  />
                )}

                {question.type === 'upload' && (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50">
                    <span className="text-2xl">📎</span>
                    <p className="text-gray-600 mt-2 text-sm">Click to upload file</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Close Preview */}
        <div className="flex justify-end">
          <button
            onClick={() => setActiveView('list')}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            ← Back to List
          </button>
        </div>

        {/* Assignment Modal */}
        {renderAssignmentModal()}
      </div>
    );
  }
};

export default ConsentFormsManager;
